// Provenance: repo:tds-reconciliation-portal:frontend/src/shell/Layout.tsx (ADR-0005) —
// REBUILT for the estate chrome (ADR-0010/ADR-0037): the platform shell owns the top bar and
// the app sidebar, so the predecessor's own AppShell becomes an IN-PAGE frame — client and FY
// identity on one row, the workspace tabs under it, the page below. The wrong-run plumbing
// (withRun on every tab link) and the session-loaded badges carry unchanged.
import { useMemo } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'

import { useClient, useDocuments, usePeriods, usePeriodSummary } from '../api/hooks'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { useRunSelection } from './runContext'
import { useWorkspaceMatch } from './workspace'
import { loadedRows, reportsBadge, useWorkspaceSession } from './workspaceSession'

const fmt = new Intl.NumberFormat('en-IN')

interface Tab {
  key: string
  label: string
  /** run-dependent tabs carry ?run= so moving between surfaces never
   *  silently switches which run you are looking at */
  withRun?: boolean
  badge?: number
}

export function Workspace() {
  const ws = useWorkspaceMatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { sides } = useWorkspaceSession()
  const { runId, withRun } = useRunSelection(ws?.periodId ?? null)

  const clientQ = useClient(ws?.clientId ?? '')
  const periodsQ = usePeriods(ws?.clientId ?? '')
  const summaryQ = usePeriodSummary(ws?.periodId ?? '')
  const docsQ = useDocuments(runId ?? '', runId != null)

  const loaded = ws ? sides(ws.periodId) : { source: false, book: false }
  const rawRows = ws ? loadedRows(summaryQ.data, loaded) : 0
  const reports = reportsBadge(runId, docsQ.data?.length)

  const tabs: Tab[] = useMemo(
    () => [
      { key: 'upload', label: 'Upload' },
      { key: 'raw-data', label: 'Raw data', badge: rawRows || undefined },
      { key: 'aliases', label: 'Party matching', withRun: true },
      { key: 'reconciliation', label: 'Reconciliation', withRun: true },
      { key: 'reports', label: 'Reports', withRun: true, badge: reports },
      { key: 'approvals', label: 'Approvals' },
      { key: 'settings', label: 'Settings' },
      { key: 'audit', label: 'Audit' },
    ],
    [rawRows, reports],
  )

  if (!ws) return null

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-center gap-3 border-b border-border pb-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-foreground">
            {clientQ.data?.legal_name ?? summaryQ.data?.client?.name ?? '…'}
          </h1>
          <p className="text-xs text-muted-foreground">
            {summaryQ.data?.period?.label ?? 'workspace'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor="fy-select">
            FY
          </label>
          <select
            id="fy-select"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={ws.periodId}
            onChange={(e) => {
              const v = e.target.value
              if (v && v !== ws.periodId) {
                void navigate(`/w/${ws.clientId}/${v}/${ws.section || 'upload'}`)
              }
            }}
            aria-label="Financial year"
          >
            {(periodsQ.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <nav className="mb-4 flex flex-wrap gap-1 border-b border-border" aria-label="Workspace">
        {tabs.map((t) => {
          const href = t.withRun ? withRun(`${ws.base}/${t.key}`) : `${ws.base}/${t.key}`
          return (
            <NavLink
              key={t.key}
              to={href}
              className={({ isActive }) =>
                `-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                  isActive
                    ? 'border-primary font-semibold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`
              }
            >
              {t.label}
              {t.badge != null && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {fmt.format(t.badge)}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Inside the frame, so a page that throws leaves the tabs standing and
          you can navigate away from it. Keyed on the path: moving to another
          screen clears the caught error. */}
      <ErrorBoundary resetKey={location.pathname}>
        <Outlet />
      </ErrorBoundary>
    </div>
  )
}
